import { Event, useStore } from '@mirai/data-sources';
import { dateFormat, parseDate, useLocale } from '@mirai/locale';
import { Button, Modal as ModalBase, styles, Theme, View } from '@mirai/ui';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useState } from 'react';

import { getModalActionConfig } from './Booking.Modal.actions';
import { ERROR } from '../../../Core.constants';
import { getSafeTrust } from '../../../trust';
import { EVENT } from '../../helpers';
import { ACTION, FORMAT_DATE } from '../Booking.constants';
import { L10N } from '../Booking.l10n';
import * as style from '../Booking.module.css';

const ACTION_VALUES = Object.values(ACTION);

const Modal = ({
  action,
  dataSource: { id, pinCode, token, ...dataSource } = {},
  onChange = () => {},
  onClose,
  ...others
}) => {
  const { translate } = useLocale();

  const { value: store = {} } = useStore();
  const { urlParams: { idHotel } = {} } = store;

  const [busy, setBusy] = useState();
  const [error, setError] = useState();
  const [form, setForm] = useState({});
  const [visible, setVisible] = useState(false);
  const config = getModalActionConfig(action);

  useEffect(() => {
    if (action) return setVisible(true);

    setForm({});
  }, [action]);

  const handleChange = useCallback((next) => {
    setForm((previous) => ({ ...previous, ...next }));
  }, []);

  const handleError = (error) => {
    setError(error);
  };

  const handleSubmit = async () => {
    setBusy(true);
    const response = await config.request({ form, id, idHotel, pinCode, token }).catch((error) => {
      const isWarning = error?.code === 404 && config.warningOn404;

      Event.publish(EVENT.NOTIFICATION, {
        error: !isWarning,
        warning: isWarning,
        defaultMessage: translate(
          isWarning ? L10N.NOTIFICATION_WARNING_NO_AVAILABILITY : L10N.NOTIFICATION_ERROR[ERROR.UNKNOWN],
          {
            checkIn: dateFormat(parseDate(form?.checkIn), FORMAT_DATE),
            checkOut: dateFormat(parseDate(form?.checkOut), FORMAT_DATE),
          },
        ),
      });
    });
    setBusy(false);

    if (!response) return;

    getSafeTrust(store).booking(
      { ...dataSource, ...response, id, state: config.state || response.state },
      store,
      action === ACTION.CANCEL ? 'cancelled' : 'observed',
    );

    Event.publish(EVENT.NOTIFICATION, {
      success: true,
      ...config.successL10N,
    });
    handleClose();
    onChange({ response, state: config.state });
  };

  const handleClose = (nextAction) => {
    const { motionExpand = '0.3s' } = Theme.get();
    const action = ACTION_VALUES.includes(nextAction) ? nextAction : undefined;

    setVisible(false);
    setTimeout(() => onClose(action), parseFloat(motionExpand.replace('s')) * 1000);
  };

  return (
    <ModalBase
      {...others}
      fit={config.fit}
      preventDefault={false}
      title={translate(config.titleL10N)}
      visible={visible}
      onClose={handleClose}
      className={styles(config.className, others.className)}
    >
      <View className={styles(style.modal, config.editLayout && style.edit)}>
        {React.createElement(config.Form, {
          dataSource,
          form,
          ...config.getFormProps?.(others),
          onChange: handleChange,
          onError: handleError,
        })}
        <View row className={styles(style.buttons, config.editLayout && style.edit)}>
          <Button large secondary wide onPress={() => handleClose(config.backAction)} testId="cancel-button">
            {translate(L10N.ACTION_BACK)}
          </Button>
          <Button
            busy={busy}
            disabled={config.disableSubmitOnError && !!error}
            large
            wide
            onPress={handleSubmit}
            testId="submit-button"
          >
            {translate(config.submitL10N)}
          </Button>
        </View>
      </View>
    </ModalBase>
  );
};

Modal.displayName = 'Mirai:Core:Booking:Modal';

Modal.propTypes = {
  action: PropTypes.oneOf(Object.values(ACTION)),
  dataSource: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    pinCode: PropTypes.number,
    token: PropTypes.string,
  }),
  onChange: PropTypes.func,
  onClose: PropTypes.func,
};

export { Modal };
